/**
 * Who is looking at the report, and what they are looking at — derived from the boot values rather
 * than handed down.
 *
 * These used to be computed in the legacy shell and threaded into React as props, which meant the
 * same expression written in a dozen coffee files and carried through components that only pass it
 * on. The values come from `PageGlobals`, so a surface that needs one asks here instead.
 *
 * Functions rather than constants, for the reason `page-globals` gives: a constant is evaluated when
 * the module first loads, which is right on the served page and wrong everywhere the page is
 * fabricated.
 */

import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

const roles = () => PageGlobals.USER_ROLES || []

export const viewerIsAdmin = () => roles().includes('admin')
export const viewerIsOwner = () => roles().includes('owner')
export const viewerIsViewer = () => roles().includes('viewer')
export const viewerIsGuest = () => roles().includes('guest')

/** A share-link reader, who arrives with a key instead of a session. */
export const isSharedView = () => undefined !== PageGlobals.SHARE_KEY

export const surveyIsCompleted = () => Boolean(PageGlobals.SURVEY_FINISHED_AT)
export const surveyIsExternal = () => PageGlobals.IS_EXTERNAL_SURVEY

/**
 * What the header reports under "Completed": the finish timestamp when there is one, and otherwise
 * the nearest description of the data the report was built from.
 */
export const surveyCompletionLabel = () =>
  PageGlobals.SURVEY_FINISHED_AT ||
  (PageGlobals.SURVEY_STARTED_AT && 'in progress') ||
  (PageGlobals.HAS_FAKE_RESPONSES && 'artificial responses') ||
  'N/A'

/**
 * The viewer fields the sidebar's model carries. Filled at the render boundary rather than threaded
 * in, so the panels below keep reading `model.isAdmin` and friends unchanged.
 */
export const viewerModelFields = () => ({
  isAdmin: viewerIsAdmin(),
  isSurveyOwner: viewerIsOwner(),
  isSurveyGuest: viewerIsGuest(),
  isSurveyViewer: viewerIsViewer(),
  isSurveyCompleted: surveyIsCompleted(),
  isExternalSurvey: surveyIsExternal()
})

/** The viewer flags the specials surface takes as top-level props. */
export const viewerProps = () => ({
  isAdmin: viewerIsAdmin(),
  isSurveyGuest: viewerIsGuest(),
  isSurveyOwner: viewerIsOwner()
})

